import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { API } from './constants';

const EMPTY_FORM = {
  name: '',
  email: '',
  phone: '',
  event_type: '',
  event_date: '',
  budget_range: '',
  services_needed: [],
  notes: '',
  source: 'inquiry',
};

export const ClientModal = ({ open, onClose, client, onSave }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    if (client) {
      setFormData({
        name: client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        event_type: client.event_type || '',
        event_date: client.event_date || '',
        budget_range: client.budget_range || '',
        services_needed: client.services_needed || [],
        notes: client.notes || '',
        source: client.source || 'inquiry',
      });
    } else {
      setFormData(EMPTY_FORM);
    }
  }, [client, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (client) {
        await axios.put(`${API}/crm/clients/${client.crm_id}`, formData, { withCredentials: true });
        toast.success('Client updated');
      } else {
        await axios.post(`${API}/crm/clients`, formData, { withCredentials: true });
        toast.success('Client created');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save client');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-white">
            {client ? 'Edit Client' : 'Add New Client'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Name *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors"
                placeholder="Client name"
              />
            </div>
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Email *</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors"
                placeholder="email@example.com"
              />
            </div>
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors"
                placeholder="(123) 456-7890"
              />
            </div>
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Event Type *</label>
              <input
                type="text"
                required
                value={formData.event_type}
                onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors"
                placeholder="Wedding, Corporate, etc."
              />
            </div>
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Event Date</label>
              <input
                type="date"
                value={formData.event_date}
                onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:outline-none focus:border-[#D4AF37] transition-colors"
              />
            </div>
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Budget Range</label>
              <Select
                value={formData.budget_range}
                onValueChange={(value) => setFormData({ ...formData, budget_range: value })}
              >
                <SelectTrigger className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:ring-0 text-white h-auto">
                  <SelectValue placeholder="Select budget" />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10">
                  {['Under $10,000', '$10,000 - $25,000', '$25,000 - $50,000', '$50,000 - $100,000', '$100,000+'].map((range) => (
                    <SelectItem key={range} value={range} className="text-white/70 focus:bg-[#050505]">
                      {range}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Source</label>
              <Select
                value={formData.source}
                onValueChange={(value) => setFormData({ ...formData, source: value })}
              >
                <SelectTrigger className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:ring-0 text-white h-auto">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10">
                  {['inquiry', 'referral', 'website', 'social', 'other'].map((source) => (
                    <SelectItem key={source} value={source} className="text-white/70 focus:bg-[#050505] capitalize">
                      {source}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full bg-transparent border border-white/20 p-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors min-h-[100px] resize-none"
                placeholder="Additional notes..."
              />
            </div>
          </div>
          <button type="submit" className="w-full py-4 bg-[#1B1B1B] text-white text-sm uppercase tracking-widest hover:bg-[#111111] transition-colors">
            {client ? 'Update Client' : 'Create Client'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
